import './login.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { ILogin } from './login.interface';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { Util } from '../../../utils/util';
import { FieldType, Modals, DataLayerErrorType } from '../../../types/enums';
import { ProfileService } from '../../../services/profile-service';
import { MfaMessage } from '../../../services/profile-service.interface';
import { Modal } from '../modal';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { DialogCreators } from '../../../utils/dialog-creators';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { InputValidator } from '../../../validators/input-validator';
import { IValidationField } from '../../../validators/validation-field.interface';
import { Data } from '../../../models/data';
import { Passcode } from '../Passcode/passcode';
import { GoogleSignInService } from '../../../services/google-signin-service';
import { awaitNativeOnce } from '../../../utils/native-bridge';
import { AppCapability, deviceSupportsAppCapability, hasAppCapability } from '../../../utils/app-capabilities';

/**
 * Represents the login modal for user authentication.
 */
export class Login extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;
    private readonly _item: ILogin;
    private readonly _bindingHelper: TwoWayBindingHelper<ILogin>;
    private _shouldDisplayValidationMessages: boolean = false;
    private _isExternalRedirect: boolean = false;
    /** Re-proving an existing Google session before adding a first password (US 73700). */
    private _isReauthorize: boolean = false;

    private readonly _googleSignInService: GoogleSignInService;
    private _googleButtonRendered: boolean = false;

    /**
     * Initializes a new instance of the Login modal.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {ProfileService} profileService - The service handling user authentication.
     */
    constructor(eventBus: EventEmitter, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService, data: Data) {
        super("modal_login", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Sign In" }, Modals.LOGIN);
        this._profileService = profileService;
        this._item = this._initializeModel();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._data = data;
        this._googleSignInService = new GoogleSignInService(context.getProperty("googleOAuthClientId"));
        this._setupEventBusListeners();
        this._setEventListeners();
        this._setupTwoWayBinding();
    }

    _isEmailOnlySignIn(): boolean {
        return this._data.getBrandProperty("Settings").SIGNINOPT === 0;
    } 

    public override open(): void {
        this._shouldDisplayValidationMessages = false;
        this._hideErrorElements();
        this._configureSignInUI();
        this._setupGoogleSignInAsync();

        this._renderPage();
        super.open();
        Util.ensureAnnouncerPresent();
        setTimeout(() => {
            document.getElementById("txt_login_email")?.focus();
        }, 100);     
    }

    private async _setupGoogleSignInAsync(): Promise<void> {
        const isAvailable = this._profileService.loyaltyProvider.hasGoogleSignIn();

        // Only the native shells inject the bridge, so it identifies them on any page load; platform and UA don't (iPad sends a Mac UA).
        if (isAvailable && Util.isAppView() && typeof startNativeGoogleSignIn === "function") {
            Util.hideElement("div_login_google_button");
            Util.showElement("div_login_google");
            Util.showElement("btn_login_google_native");
            Util.setElement("onclick", "btn_login_google_native", this._handleNativeGoogleSignInAsync);
            return;
        }

        Util.hideElement("btn_login_google_native");
        this._googleButtonRendered = await this._googleSignInService.mountButtonAsync({
            sectionId: "div_login_google",
            host: document.getElementById("div_login_google_button"),
            alreadyRendered: this._googleButtonRendered,
            isAvailable: isAvailable,
            onCredential: (idToken: string) => void this._handleGoogleCredentialAsync(idToken),
            locale: this._context.getProperty("languageCode"),
            text: "signin_with"
        });
    }

    private readonly _handleNativeGoogleSignInAsync = (): Promise<void> =>
        this._googleSignInService.signInNativeAsync(
            () => this._profileService.getGoogleSignInNonce(),
            (idToken, nonce) => this._handleGoogleCredentialAsync(idToken, nonce),
            () => DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names("GoogleSignInFailed"), true)
        );

    /**
     * Existing customer signs straight in; one with no profile is handed to sign-up (US 47659).
     */
    private async _handleGoogleCredentialAsync(idToken: string, nonce?: string): Promise<void> {
        const loader = new LoaderSpinner();
        const result = await this._profileService.signInGoogle(idToken, true, nonce);
        loader?.destroy();

        if (!result.success) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                result.message ?? this._localizationService.names("GoogleSignInFailed"),
                true
            );
            return;
        }

        if (result.isNewProfile) {
            // Nothing was created; sign-up collects the fields Google can't supply. The nonce travels
            // with the handoff because the server rejects a native-app token without it.
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP, {
                googleIdToken: idToken,
                googleIdentity: result.googleIdentity,
                googleNonce: nonce
            });
            return;
        }

        if (this._isReauthorize) {
            // Not LOGGED_IN: that is a page-wide broadcast and the customer was already signed in.
            this._isReauthorize = false;
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CHANGE_PASSWORD, {
                googleIdToken: idToken
            });
            return;
        }

        this._eventBus.emit(EventTypes.LOGGED_IN);
    }

    /**
     * Creates the initial login model.
     * @returns {ILogin} The initial model containing login credentials.
     */
    private _initializeModel(): ILogin {
        return {
            email: "",
            password: "",
            rememberMe: false,
            countryId: 1,
            isPhone: false,
            isBiometric: false
        };
    }

    /**
     * Sets up event bus listeners for login-related events.
     */
    private _setupEventBusListeners(): void {
        this._eventBus.on(EventTypes.LOGGED_IN, () => this.close());

        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: { externalRedirect?: boolean, reauthorize?: boolean }) => {
            if (modal !== Modals.LOGIN) return;

            this._isReauthorize = options?.reauthorize ?? false;

            const isExternalRedirect = options?.externalRedirect ?? false;
            this._isExternalRedirect = isExternalRedirect;
            this._props.disableEscapeClose = isExternalRedirect;

            if (isExternalRedirect) {
                Util.showElement("btn_login_continue_guest");
                if (this._elementDelete)
                    Util.hideElement(this._elementDelete);
            } else {
                Util.hideElement("btn_login_continue_guest");
                if (this._elementDelete)
                    Util.showElement(this._elementDelete);
            }
        });
    }

    /**
     * Attaches event listeners to various UI elements.
     */
    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_login_forgot_password", this._handleForgotPasswordAsync);
        Util.setElement("onclick", "btn_login_sign_in", this._handleSignInAsync);
        Util.setElement("onclick", "btn_login_continue_guest", this._handleContinueAsGuestAsync);
        Util.setElement("onclick", "btn_login_sign_up", this._handleSignUp);
        Util.setElement("onclick", "btn_login_sign_in_facebook", this._handleFacebookSignIn);
        Util.setElement("onclick", "btn_login_sign_in_apple", this._handleAppleSignIn);
        Util.setElement("onclick", "btn_signin_privacy_terms_terms", this._openTermsOfUse);
        Util.setElement("onclick", "btn_signin_privacy_terms_privacy", this._openPrivacyPolicy);

        const validationFields: IValidationField[] = [
            { type: FieldType.EMAIL, inputId: "txt_login_email", errorId: "spn_login_email_error" },
            { type: FieldType.PASSWORD, inputId: "txt_login_password", errorId: "spn_login_password_error" }
        ];

        validationFields.forEach(field => {
            this._setInputEvents(field.inputId, field.errorId);
        });
    }

    private _setInputEvents(inputId: string, errorId: string): void {
        const inputElement = document.getElementById(inputId) as HTMLInputElement;

        if (!inputElement) return;
        inputElement.onkeyup = async (event) => {
            await this._handleEnterKeyAsync(event);
        };

        inputElement.oninput = () => {
            Util.hideElementErrors(inputId, errorId);
        };

        inputElement.onblur = () => {
            if (this._shouldDisplayValidationMessages) {
                this._validateLoginInputs();
            }
        };
    }

    _configureSignInUI = (): void => {
        const labelEmailOrPhoneNumber = document.getElementById("lbl_signin_email_phoneNo");

        if (labelEmailOrPhoneNumber) {
            if (this._isEmailOnlySignIn()) {
                labelEmailOrPhoneNumber.setAttribute('ltag', 'Email');
                labelEmailOrPhoneNumber.innerText = this._localizationService.names('Email');

                Util.showElement("div_signin_password_parent")
            } else {
                labelEmailOrPhoneNumber.setAttribute('ltag', 'EmailOrPhoneNumber');
                labelEmailOrPhoneNumber.innerText = this._localizationService.names('EmailOrPhoneNumber');
            }
        }
        Util.hideElement("div_login_text_privacy_terms");

        const countries = this._data.getBrandProperty("Countries");
        Util.populateCountryCodeSelectList("dl_login_country_code", "txt_login_email", countries, this._data.getBrandProperty("StoreInfo").countryId || 1, false);

        if (Util.isAppView())
            Util.hideElement("div_signin_remember_me");
    }


    /**
     * Sets up two-way data binding for login form inputs.
     */
    private _setupTwoWayBinding(): void {
        this._bindingHelper.bindToInput("email", "txt_login_email");
        this._bindingHelper.bindToInput("password", "txt_login_password");
        this._bindingHelper.bindToInput("rememberMe", "chk_login_remember_me");
        this._bindingHelper.bindToInput("isBiometric", "chk_login_biometric");
    }

    private _renderPage(): void {
        const loyaltyInfo = this._data.getLoyaltyInfo();
        const pointsUnit = this._localizationService.getLoyaltyUnitName(loyaltyInfo);
        const fancyTitleText = this._localizationService.names("SignInSlogan")
            .replaceAll("{{points_unit}}", pointsUnit);
        Util.setElement("innerHTML", "p_login_fancy_title", fancyTitleText);

        const rememberMeToggle = Util.isAppView() ? Util.hideElement : Util.showElement;
        rememberMeToggle("div_login_remember_me");

        // Hidden unless the app build has biometric login and the phone has biometrics set up
        const deviceCanUseBiometric = hasAppCapability(AppCapability.Biometric) && deviceSupportsAppCapability(AppCapability.Biometric);
        const showBiometricCheckbox = deviceCanUseBiometric && this._data.getBrandProperty("Settings").OOS.biometric;
        const biometricToggle = showBiometricCheckbox ? Util.showElement : Util.hideElement;
        biometricToggle("div_login_biometric");
    }

    private readonly _signInByPhone = async (code: string): Promise<boolean> => {

        const isApp = Util.isAppView();

        const parsedPhoneNumber = this._item.email.replaceAll(/[-()]/g, "");
        const countryId = Number(this._item.countryId);
        const rememberMe = this._item.rememberMe;

        const loader = new LoaderSpinner();
        const success = await this._profileService.signInPhone({ store_key: this._data.getBrandProperty("StoreInfo")?.storeKey, phone: parsedPhoneNumber, country_id: countryId, code: code, app: isApp, remember_me: rememberMe });
        loader?.destroy();

        if (success) {
            this.close();

            if (this._isExternalRedirect)
                this._handleContinueAsGuestAsync();

            if (this._dataLayer)
                this._dataLayer.sign_in(this._profileService.user.user, 'phone');
        } else {
            this._dataLayer?.error_message(
                this._localizationService.names("InvalidEmailOrPassword"),
                DataLayerErrorType.USER_ERROR
            );
        }
        return success;
    }


    private _passcodeModal: Passcode | undefined;

    private readonly _handlePhoneSigninAsync = async (): Promise<void> => {
        const parsedPhoneNumber = this._item.email.replaceAll(/[-()]/g, "");
        const countryId = Number(this._item.countryId);
        const result = await this._profileService.getPhoneCode(parsedPhoneNumber, countryId);

        if (!result.success) {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names(result.message));
            return;
        }

        if (!this._passcodeModal) {

            this._passcodeModal = new Passcode(
                this._eventBus,
                this._context,
                this._localizationService,
                this._dataLayer,
                async () => { await this._profileService.getPhoneCode(parsedPhoneNumber, countryId); },
                (x) => this._signInByPhone(x)
            );

            this._passcodeModal.init();
        }

        this._passcodeModal.open();
    }

    /**
     * Handles user login when the sign-in button is clicked.
     */
    private readonly _handleSignInAsync = async (): Promise<void> => {

        Util.resetAnnounceCycle();

        Util.beginBatchAnnouncement();
        const isValid = this._validateLoginInputs();
        Util.endBatchAnnouncement();

        if (!isValid) {
            Util.scrollToFirstInvalidElement("modal_login");
            Util.announceErrors(Util.collectVisibleErrors("modal_login"));
            return;
        }


        if (this._item.isPhone) {
            await this._handlePhoneSigninAsync();
        } else {
            await this._handleEmailSignInAsync();
        }
    }

    private async _handleEmailSignInAsync(): Promise<void> {
        const wantsBiometricEnrollment = this._item.isBiometric;

        const loader = new LoaderSpinner();
        const result = await this._profileService.signIn({
            email: this._item.email,
            password: this._item.password,
            rememberMe: this._item.rememberMe
        });
        loader?.destroy();

        if (result.success) {
            this.close();

            if (this._isExternalRedirect)
                this._handleContinueAsGuestAsync();

            if (this._dataLayer)
                this._dataLayer.sign_in(this._profileService.user.user, 'email');

            this._enrollBiometricIfRequested(wantsBiometricEnrollment);
            return;
        }

        // Closing the MFA prompt isn't a failed sign-in, so it shows no error.
        if (result.mfaCancelled)
            return;

        if (result.message === MfaMessage.TooManyAttempts) {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names(MfaMessage.TooManyAttempts));
            return;
        }

        Util.showElement("spn_login_error");

        Util.announceErrors(Util.collectVisibleErrors("modal_login"));

        if (this._dataLayer) {
            this._dataLayer.error_message(
                this._localizationService.names("InvalidEmailOrPassword"),
                DataLayerErrorType.USER_ERROR
            );
        }
    }

    /**
     * If the "Use Biometric Log In" checkbox (only shown in-app when Setting 274's biometric
     * flag is on) is checked, runs the native enrollment challenge with the credential from the
     * sign-in that just completed. Fire-and-forget -- doesn't block or fail the sign-in itself.
     */
    private readonly _enrollBiometricIfRequested = (wantsBiometricEnrollment: boolean): void => {
        if (!wantsBiometricEnrollment || typeof enrollBiometricLogin !== "function")
            return;

        void awaitNativeOnce<{ success: boolean; reason: string } | null>({
            name: "EnrollBiometricLogin",
            setResolver: (r) => { globalThis.pendingBiometricEnrollResolve = r; },
            clearResolver: () => { globalThis.pendingBiometricEnrollResolve = null; },
            invokeNative: () => enrollBiometricLogin!(),
            timeoutValue: null,
            timeoutMs: 30000 // biometric prompt needs time for user interaction
        });
    }

    /**
     * Handles forgot password logic when the "Forgot Password" button is clicked.
     */
    private readonly _handleForgotPasswordAsync = async (): Promise<void> => {
        if (!this._validateForgotPasswordInput()) {
            Util.scrollToFirstInvalidElement("modal_login");
            return;
        }

        const loader = new LoaderSpinner();
        const success = await this._profileService.forgotPassword(this._item.email);
        loader?.destroy();

        if (success) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("PasswordReset").replace("{email}", this._item.email),
            );
        } else {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("UnableResetPassword"),
                true
            );
        }
    }

    /**
    * Handles the Enter key press event on the sign-in form.
    * @private
    * @param {any} event - The key up event object.
    * @returns {void}
    */
    private readonly _handleEnterKeyAsync = async (event: KeyboardEvent): Promise<void> => {
        if (event.key === "Enter") {
            event.preventDefault();
            await this._handleSignInAsync();
            return;
        }

        const normalizedValue = this._item.email.replace(/\s/g, "");
        const isPhoneFormat =
            !this._isEmailOnlySignIn() &&
            /^(\(\d{3}\)|\d{3})-?\d{3}-?\d{4}$/.test(normalizedValue);

        const parsedPhoneNumber = this._item.email.replace(/[-()\s]/g, "");
        const shouldUsePhoneFlow =
            isPhoneFormat && parsedPhoneNumber.length > 9 && parsedPhoneNumber.length < 12;

        if (shouldUsePhoneFlow) {
            Util.showElement("div_login_text_privacy_terms");
            Util.showElement("sel_login_country_code_parent");
            Util.hideElement("div_login_password_parent");
            Util.setElementClass("REMOVE", "country_code_email_phone_row", "gridGap0");
            this._item.isPhone = true;
            return;
        }

        Util.hideElement("div_login_text_privacy_terms");
        Util.hideElement("sel_login_country_code_parent");
        Util.showElement("div_login_password_parent");
        Util.setElementClass("ADD", "country_code_email_phone_row", "gridGap0");
        this._item.isPhone = false;
    }

    /**
     * Handles the sign-in process using Facebook.
     */
    private readonly _handleFacebookSignIn = (): void => {
        // 2DO: US #47654
    }

    /**
     * Handles the sign-in process using Apple ID.
     */
    private readonly _handleAppleSignIn = (): void => {
        // 2DO: US #47655
    }

    private readonly _handleContinueAsGuestAsync = (): void => {
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ORDER_TYPE_AND_TIME);
    }

    /**
     * Handles navigation to the signup modal.
     */
    private readonly _handleSignUp = (): void => {
        this.close();
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP)
    }

    /**
     * Hides all error messages from the login form.
     */
    private _hideErrorElements(): void {
        Util.hideElement("spn_login_error");
        Util.hideElementErrors("txt_login_email", "spn_login_email_error");
        Util.hideElementErrors("txt_login_password", "spn_login_password_error");
        document.getElementById("spn_login_email_forgot_error")?.classList.toggle("hidden", true)
    }

    /**
     * Validates the login input fields.
     * @returns {boolean} - `true` if validation passes, otherwise `false`.
     */
    private _validateLoginInputs(): boolean {
        this._shouldDisplayValidationMessages = true;
        this._hideErrorElements();

        let validationFields: IValidationField[] = [];

        if (!this._item.isPhone) {
            validationFields.push({ type: FieldType.PASSWORD, inputId: "txt_login_password", errorId: "spn_login_password_error" });
        }

        validationFields.push(
            {
                type: this._item.isPhone ? FieldType.PHONE : FieldType.EMAIL,
                inputId: "txt_login_email",
                errorId: "spn_login_email_error"
            }
        );

        return InputValidator.validateFields(validationFields, undefined, undefined, this._localizationService, this._dataLayer);
    }

    private _validateForgotPasswordInput(): boolean {
        this._hideErrorElements();

        const validationFields: IValidationField[] = [
            { type: FieldType.EMAIL, inputId: "txt_login_email", errorId: "spn_login_email_Error" }
        ];

        return InputValidator.validateFields(validationFields, undefined, undefined, this._localizationService, this._dataLayer);
    }

    private readonly _openTermsOfUse = () => {
        if (Util.isAppView() && typeof openExternalLink == "function") {
            openExternalLink(this._data.getBrandProperty("Settings").OOS.termsOfUseUrl);
        } else {
            window.open(this._data.getBrandProperty("Settings").OOS.termsOfUseUrl, "_blank", "noopener, noreferrer");
        }
    }

    private readonly _openPrivacyPolicy = () => {
        if (Util.isAppView() && typeof openExternalLink == "function") {
            openExternalLink(this._data.getBrandProperty("Settings").OOS.privacyPolicyUrl);
        } else {
            window.open(this._data.getBrandProperty("Settings").OOS.privacyPolicyUrl, "_blank", "noopener, noreferrer");
        }
    }
}
